import * as React from "react";
import { connect } from "react-redux";
import * as metaReducer from "../../reducers/metaReducer";
import StepGuideSidebar from "../../components/StepGuide/StepGuideSidebarComponent";
import { IStep, StepType } from "../../components/StepGuide/StepGuideComponent";
import Card from "../../components/DataDisplay/CardComponent";
import i18n from "../../i18n/i18n";
import IInvoiceVm from "../../models/home/Payment/IInvoiceVm";
import { IToastrMessage } from "../../containers/Shared/ToastrContainer";
import { IMeta } from "../../reducers/metaReducer";
import * as PropTypes from "prop-types";
import { ApplicationSubmitStatusType } from "../../models/enums/ApplicationSubmitStatusType";
import { PaymentType } from "../../models/enums/PaymentType";
import IErrorVm from "../../models/home/Error/IErrorVm";
import ICompanyVm from "../../models/shared/ICompanyVm";
import * as ICompanyVmInstance from "../../models/shared/ICompanyVm";
import ICountryVm from "../../models/shared/ICountryVm";
import * as IAddressVmInstance from "../../models/shared/IAddressVm";
import * as companyReducer from "../../reducers/companiesReducer";
import * as invoiceReducer from "../../reducers/invoiceReducer";
import * as addressReducer from "../../reducers/addressReducer";
import OnlinePaymentForCitizenComponent from "./components/OnlinePaymentForCitizenComponent";
import OnlineBulkPaymentForCompanyComponent from "./components/OnlineBulkPaymentForCompanyComponent";
import IParentBulkInvoiceVm from "../../models/home/Payment/IParentBulkInvoiceVm";
import OnlinePaymentForCompanyComponent from "./components/OnlinePaymentForCompanyComponent";
import ReceiptStep from "./components/ReceiptStepComponent";

interface IOnlinePaymentProps {
  invoice?: IInvoiceVm | IParentBulkInvoiceVm;
  selectedPayor?: SelectedPayorType;
  currentStepId?: number;
  validationErrors?: IErrorVm[];
  serverErrors?: IErrorVm[];
}

interface IOnlinePaymentDispatch {
  updateCompanyRegistrationIdentifier: (value: string, invoiceId: string) => void;
  replaceCompany: (company: ICompanyVm, invoiceId: string) => void;
  updateSelectedPayor: (payorType: SelectedPayorType, invoice: IInvoiceVm) => void;
  updateCompanyName: (value: string, invoiceId: string) => void;
  updateCoName: (value: string, addressId: string) => void;
  updateCombinedAddressLine: (value: string, addressId: string) => void;
  updatePostalDistrict: (value: string, addressId: string) => void;
  updatePostalCode: (value: string, addressId: string) => void;
  updateAreTermsAndConditionsAccepted: (value: boolean, invoiceId: string) => void;
  updateCountry: (country: ICountryVm, addressId: string) => void;
  setToastMessage: (message: IToastrMessage) => void;
}

export enum SelectedPayorType {
  CitizenPaying = 1,
  CompanyPaying = 2,
}

type AllProps = IOnlinePaymentProps & IOnlinePaymentDispatch;

class OnlinePayment extends React.Component<AllProps> {
  constructor(props: AllProps) {
    super(props);
  }

  public static childContextTypes: React.ValidationMap<any> = {
    errors: PropTypes.array.isRequired,
  };

  public getChildContext() {
    return {
      errors: this.props.validationErrors || [],
    };
  }

  public componentDidMount() {
    // If context contains errors, show them for the user
    this.props.serverErrors.forEach((e) => {
      this.props.setToastMessage({
        message: e.message,
        type: "error",
      } as IToastrMessage);
    });
  }

  private getContent(invoice: IInvoiceVm | IParentBulkInvoiceVm, selectedPayor: SelectedPayorType) {
    const { validationErrors, updateCompanyRegistrationIdentifier, updateSelectedPayor, updateCompanyName, updateCoName, updateCombinedAddressLine, updatePostalDistrict, updatePostalCode, updateCountry, updateAreTermsAndConditionsAccepted, replaceCompany, setToastMessage } = this.props;

    switch (invoice.paymentType) {
      case PaymentType.OnlinePaymentForCitizen:
        return (
          <OnlinePaymentForCitizenComponent
            selectedPayor={selectedPayor}
            invoice={invoice}
            validationErrors={validationErrors}
            onUpdateCompanyRegistrationIdentifier={(value) => updateCompanyRegistrationIdentifier(value, invoice.id)}
            onUpdateSelectedPayor={(payorType) => updateSelectedPayor(payorType, invoice)}
            onUpdateCompanyName={(value) => updateCompanyName(value, invoice.id)}
            onUpdateCoName={(value) => updateCoName(value, invoice.companyBilled.address.id)}
            onUpdateCombinedAddressLine={(value) => updateCombinedAddressLine(value, invoice.companyBilled.address.id)}
            onUpdatePostalDistrict={(value) => updatePostalDistrict(value, invoice.companyBilled.address.id)}
            onUpdatePostalCode={(value) => updatePostalCode(value, invoice.companyBilled.address.id)}
            onUpdateCountry={(country) => updateCountry(country, invoice.companyBilled.address.id)}
            onUpdateAreTermsAndConditionsAccepted={(value) => updateAreTermsAndConditionsAccepted(value, invoice.id)}
            onReplaceCompany={(company) => replaceCompany(company, invoice.id)}
            onSetToastMessage={(message) => setToastMessage(message)}
          />
        );
      case PaymentType.OnlinePaymentForCompany:
        return <OnlinePaymentForCompanyComponent invoice={invoice} validationErrors={validationErrors} onUpdateAreTermsAndConditionsAccepted={(value) => updateAreTermsAndConditionsAccepted(value, invoice.id)} />;
      case PaymentType.OnlineBulkPaymentForCompany:
      case PaymentType.GiroformOrOnlineBulkPaymentForCompany:
        return <OnlineBulkPaymentForCompanyComponent invoice={invoice as IParentBulkInvoiceVm} validationErrors={validationErrors} onUpdateAreTermsAndConditionsAccepted={(value) => updateAreTermsAndConditionsAccepted(value, invoice.id)} />;
      default:
        return <div></div>;
    }
  }

  private buildSteps() {
    const { invoice, currentStepId, selectedPayor } = this.props;

    return [
      {
        id: 1,
        completed: currentStepId === 3,
        content: <div data-ui-test="payment-step-invoice">{this.getContent(invoice, selectedPayor)}</div>,
        title: i18n.t("TEXT_INVOICE"),
        type: StepType.Step,
      },
      {
        id: 2,
        completed: currentStepId === 3,
        title: i18n.t("TEXT_PAYMENT"),
        type: StepType.Step,
      },
      {
        id: 3,
        completed: currentStepId === 3,
        content: <ReceiptStep invoice={invoice} />,
        title: i18n.t("TEXT_RECEIPT"),
        type: StepType.Receipt,
      },
    ] as IStep[];
  }

  public render() {
    const { currentStepId } = this.props;

    document.title = i18n.t("TEXT_WEBSITE_TITLE_PAYMENT");

    const steps = this.buildSteps();

    return (
      <div className="row-fluid">
        <div className="span3">
          <StepGuideSidebar steps={steps} currentStepNumber={currentStepId} submittedByCitizen={ApplicationSubmitStatusType.NotSet} isAsyncRunning={false} />
        </div>
        <div className="span9">
          {steps &&
            steps
              .filter((step) => step.id === currentStepId)
              .map((step) => (
                <Card key={step.id} title={step.title}>
                  {step.content}
                </Card>
              ))}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: { invoice: IInvoiceVm | IParentBulkInvoiceVm; meta: IMeta }): IOnlinePaymentProps => {
  return {
    invoice: state.invoice,
    selectedPayor: state.meta.selectedPayor || SelectedPayorType.CitizenPaying,
    currentStepId: state.meta.currentStepId || 1,
    validationErrors: state.meta.errors ? state.meta.errors.filter((err) => err.id === state.invoice.id) : [],
    serverErrors: (window as any).errors || [],
  };
};

const mapDispatchToProps = (dispatch: any): IOnlinePaymentDispatch => {
  return {
    updateCompanyRegistrationIdentifier: (value: string, invoiceId: string) => {
      dispatch(companyReducer.updateCompanyRegistrationIdentifier(value, invoiceId));
    },
    replaceCompany: (company: ICompanyVm, invoiceId: string) => {
      dispatch(companyReducer.replaceCompany(company, invoiceId));
    },
    updateSelectedPayor: (payorType: SelectedPayorType, invoice: IInvoiceVm) => {
      if (payorType === SelectedPayorType.CompanyPaying && !invoice.companyBilled) {
        const newCompany = {
          ...ICompanyVmInstance.empty(),
          id: Math.random().toString(),
          address: {
            ...IAddressVmInstance.empty(),
            id: Math.random().toString(),
            countryCode: "DK",
            countryName: "Danmark",
          },
        };
        dispatch(invoiceReducer.replaceCompanyBilled(newCompany, invoice.id));
      }
      dispatch(metaReducer.updateSelectedPayor(payorType));
    },
    updateCompanyName: (value: string, invoiceId: string) => {
      dispatch(companyReducer.updateName(value, invoiceId));
    },
    updateCoName: (value: string, addressId: string) => {
      dispatch(addressReducer.updateCoName(value, addressId));
    },
    updateCombinedAddressLine: (value: string, addressId: string) => {
      dispatch(addressReducer.updateCombinedStreetAddressLine(value, addressId));
    },
    updatePostalDistrict: (value: string, addressId: string) => {
      dispatch(addressReducer.updatePostalDistrict(value, addressId));
    },
    updatePostalCode: (value: string, addressId: string) => {
      dispatch(addressReducer.updatePostalCode(value, addressId));
    },
    updateAreTermsAndConditionsAccepted: (value: boolean, invoiceId: string) => {
      dispatch(invoiceReducer.updateTermsAndConditionsAccepted(value, invoiceId));
    },
    updateCountry: (country: ICountryVm, addressId: string) => {
      dispatch(addressReducer.updateCountry(country, addressId));
    },
    setToastMessage: (toastrMessage: IToastrMessage) => {
      dispatch(metaReducer.addToastr(toastrMessage));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OnlinePayment);
